import { Box, Flex, Text } from '@chakra-ui/react'
import { ResponsiveBarCanvas } from '@nivo/bar'
import { IResponseBase } from 'api/types'
import { Select, SelectOption } from 'components/Select'
import { useTableChartFilters } from 'contexts/TableChartFilters'
import _ from 'lodash'
import { useMemo, useState } from 'react'
import CardContainer from '../Shared/CardContainer'
import { procurementOptions, xAxisOptions } from './util'
import { compactNumberWithPrefix } from 'utils/formatNumber'

const QuantityBarChart = ({
  data,
  loading,
}: {
  data: IResponseBase[]
  loading: boolean
}) => {
  const [expanded, setExpanded] = useState<boolean>(false)
  const [hasFiltered, setHasFiltered] = useState<boolean>(false)
  const { addToFilters, tableFilters } = useTableChartFilters()

  const [selectedStackBy, setSelectedStackBy] = useState<SelectOption<string>>({
    value: 'diseaseName',
    label: 'Diseases',
  })

  const [selectedXAxis, setSelectedXAxis] = useState<SelectOption<string>>({
    value: 'yearDelivered',
    label: 'Year',
  })

  const uniqueItems = useMemo(
    () => [
      ...new Set(
        data
          .flatMap((item) => item[selectedStackBy.value])
          .map((x) => x?.toString() ?? '')
      ),
    ],
    [data, selectedStackBy.value]
  )

  const chartData = useMemo(() => {
    const mappedData = Object.entries(_.groupBy(data, selectedXAxis.value)).map(
      ([groupKey, grouped]) => {
        const result: any = {
          [selectedXAxis.value]: groupKey === 'null' ? 'Unknown' : groupKey,
        }

        // Initialize all disease names with 0
        uniqueItems.forEach((uniqueStackItem) => {
          result[uniqueStackItem] = 0
        })

        // Count occurrences for each disease name in this group
        grouped.forEach((item) => {
          const diseaseNames = item[selectedStackBy.value]
          if (Array.isArray(diseaseNames)) {
            diseaseNames.forEach((diseaseName) => {
              if (diseaseName && result.hasOwnProperty(diseaseName)) {
                result[diseaseName.toString()] += item['quantity'] ?? 0
              }
            })
          } else if (diseaseNames && result.hasOwnProperty(diseaseNames)) {
            result[diseaseNames.toString()] += item['quantity'] ?? 0
          }
        })

        return result
      }
    )

    // Only show x axis when data exists
    return mappedData.filter((x) =>
      Object.values(x).some((y) => typeof y === 'number' && y > 0)
    )
  }, [data, uniqueItems, selectedXAxis.value, selectedStackBy.value])

  return (
    <CardContainer
      className='clinicalTrialsPerMonth'
      expanded={expanded}
      setExpanded={setExpanded}
      loading={loading}
      cardProps={{ minWidth: '430px' }}
    >
      <Box>
        <Flex alignItems={'center'} h='max-content'>
          <Select
            value={selectedStackBy}
            isMulti={false}
            onChange={(x: any) => setSelectedStackBy(x)}
            options={procurementOptions}
            backgroundColor='transparent'
            fontSize={'13px'}
            noBorder
          />
          <Text fontSize='xs'>Index by:</Text>
          <Select
            value={selectedXAxis}
            isMulti={false}
            onChange={(x: any) => setSelectedXAxis(x)}
            options={xAxisOptions}
            backgroundColor='transparent'
            fontSize={'13px'}
            noBorder
          />
        </Flex>
        <Box w='100%' h={expanded ? '480px' : '230px'}>
          {data.length > 0 ? (
            <ResponsiveBarCanvas
              data={chartData}
              keys={uniqueItems}
              indexBy={selectedXAxis.value}
              labelSkipWidth={12}
              labelSkipHeight={12}
              axisBottom={{
                legend: selectedXAxis.label,
                legendOffset: expanded ? 52 : 20,
                legendPosition: 'middle',
                truncateTickAt: 50,
                tickRotation: -90,
                format: (v) => {
                  if (!expanded) return ''
                  return v.length > 10 ? (
                    <tspan>
                      {v.substring(0, 10) + '...'}
                      <title>{v}</title>
                    </tspan>
                  ) : (
                    v
                  )
                },
              }}
              axisLeft={{
                legend: 'Quantity',
                legendOffset: -50,
                legendPosition: 'middle',
                format: (val) => compactNumberWithPrefix(val, 3),
              }}
              valueFormat={(val) => compactNumberWithPrefix(val, 3)}
              margin={{
                top: 20,
                right: 20,
                bottom: expanded ? 70 : 30,
                left: 60,
              }}
            />
          ) : (
            <Flex h={'full'} justifyContent={'center'} alignItems={'center'}>
              <Text
                fontSize={'sm'}
                textAlign={'center'}
                fontWeight={'semibold'}
                color='gray.600'
              >
                No time series data found for current selection
              </Text>
            </Flex>
          )}
        </Box>
      </Box>
    </CardContainer>
  )
}

export default QuantityBarChart
